import classNames from 'classnames'
import Panel from '../../Panel'
import { useUIComponentsContext } from '../../Provider/UiComponentsProvider'
import { UserContributor as IUserContributor, TeamContributor } from '../types'
import { CONTRIBUTOR_RIGHTS } from '../utils'
import ContributorMoreActions from './ContributorMoreActions'
import UserContributor from './UserContributor'

interface ITeamContributorPros {
  key: string
  disabled?: boolean
  teamContributor: TeamContributor
  projectResponsibleId?: string
  resourceOwnerId?: string
  onTeamDelete?: Function
  onTeamMembersAccessChange?: Function
  onPermissionChange?: Function
  onContributorDelete?: Function
  onMakeResponsible?: Function
}

const TeamContributorCard = ({
  teamContributor,
  onTeamDelete,
  onTeamMembersAccessChange,
  disabled = false,
  projectResponsibleId,
  resourceOwnerId,
  ...rest
}: ITeamContributorPros) => {
  const { t } = useUIComponentsContext()

  const contributorRights = CONTRIBUTOR_RIGHTS(t)

  const { team, contributors } = teamContributor

  const filterContributors = (items: IUserContributor[], ids: string[]) =>
    items.filter(({ account }) => ids.includes(account?.id))

  const members = team.members ?? []

  const { teamMembers, guests } = members.reduce(
    (acc, { account, isGuest }) => {
      if (isGuest) {
        acc.guests.push(account?.id)
      } else {
        acc.teamMembers.push(account?.id)
      }
      return acc
    },
    {
      teamMembers: team.teamLead?.id ? [team.teamLead.id] : [],
      guests: [],
    } as {
      teamMembers: string[]
      guests: string[]
    }
  )

  const membersContributors = filterContributors(contributors, teamMembers)

  const guestsContributors = filterContributors(contributors, guests)

  const { id, name } = team

  const actionItems = [
    {
      id,
      label: (
        <span className="text-oxford-gray-800">
          {t?.('buttons.setMembersAsViewers')}
        </span>
      ),
      action: (e: Event) => {
        e?.stopPropagation()
        onTeamMembersAccessChange?.(id, 'VIEWER')
      }
    },
    {
      id,
      label: (
        <span className="text-oxford-gray-800">
          {t?.('buttons.setMembersAsEditors')}
        </span>
      ),
      action: (e: Event) => {
        e?.stopPropagation()
        onTeamMembersAccessChange?.(id, contributorRights.EDITOR?.value)
      }
    },
    {
      id,
      label: (
        <span className="text-watermelon-500">{t?.('buttons.delete')}</span>
      ),
      action: (e: Event) => {
        e?.stopPropagation()
        onTeamDelete?.(id)
      }
    }
  ]

  return (
    <Panel
      title={name}
      buttonAs="div"
      shadowClassName=""
      labelClassName="text-sm"
      renderRight={(open: boolean) => (
        <div className="flex align-middle">
          {!disabled && (
            <ContributorMoreActions
              actionItems={actionItems}
              disabled={disabled}
            />
          )}
          <i
            className={classNames([
              'far fa-chevron-down text-oxford-gray-400 transform transition-transform text-lg',
              {
                'rotate-180': open
              }
            ])}
          />
        </div>
      )}
      btnClassName="bg-electric-violet-50"
    >
      <div className="relative py-3">
        <div className="flex flex-col gap-3">
          {membersContributors.map(({ account, access, locked }) => (
            <div className="contributors" key={account.id}>
              <UserContributor
                disabled={disabled}
                account={account}
                access={
                  account?.id === resourceOwnerId
                    ? contributorRights.OWNER?.value
                    : access
                }
                teamId={id}
                showMoreActions={false}
                disableAccessDropdown={locked}
                isProjectResponsible={account?.id === projectResponsibleId}
                {...rest}
              />
            </div>
          ))}

          {guestsContributors.length > 0 && (
            <>
              <span className="text-oxford-gray-500">
                {t?.('access.teamGuests')}
              </span>
              {guestsContributors.map(({ account, access, locked }) => (
                <>
                  <div className="contributors">
                    <UserContributor
                      disabled={disabled}
                      account={account}
                      access={access}
                      teamId={id}
                      showMoreActions={false}
                      disableAccessDropdown={locked}
                      {...rest}
                    />
                  </div>
                </>
              ))}
            </>
          )}
        </div>
      </div>
    </Panel>
  )
}

export default TeamContributorCard
